/**
 * Mobile Quick Rip fit-to-screen geometry.
 *
 * Adapted from monkeytilt-club's framework-free
 * post-rip-summary/v2/fit-to-screen.ts. The stable viewport region is the
 * measurement source; card dimensions never feed back into that region.
 */

const CARD_ASPECT = 7 / 5;
const MIN_CARD_W = 48;
const HERO_MAX_COLS = 2;
const HERO_FILL = 0.66;
const GAP_FRAME_BLEED = 0.26;
const ROW_FRAME_BLEED = 0.18;
const OUTER_FRAME_PAD = 0.2;
const FIT_SAFE = 0.98;
const SINGLE_ROW_MIN_W = 96;
const SCALE_BASE_W = 1366;
const SCALE_BASE_H = 768;
const SCALE_MAX = 1.7;
const MAX_CARD_W = 320;

export type QuickRipLayout = {
  columns: number;
  cardWidth: number;
};

export function quickRipViewportScale(width: number, height: number): number {
  const ratio = Math.min(width / SCALE_BASE_W, height / SCALE_BASE_H);
  return Math.max(1, Math.min(ratio, SCALE_MAX));
}

export function solveQuickRipCardWidth({
  availableWidth,
  availableHeight,
  columns,
  rows,
  chromeHeight = 0,
}: {
  availableWidth: number;
  availableHeight: number;
  columns: number;
  rows: number;
  chromeHeight?: number;
}): number {
  const hero = rows === 1 && columns <= HERO_MAX_COLS;
  const fill = hero ? HERO_FILL : 1;
  const byWidth =
    (availableWidth * fill) /
    (columns + GAP_FRAME_BLEED * Math.max(0, columns - 1));
  const byHeight =
    (availableHeight * fill - rows * chromeHeight) /
    (rows * CARD_ASPECT + ROW_FRAME_BLEED * Math.max(0, rows - 1));
  return Math.max(MIN_CARD_W, Math.min(byWidth, byHeight));
}

export function chooseQuickRipLayout({
  availableWidth,
  availableHeight,
  count,
  chromeHeight = 0,
}: {
  availableWidth: number;
  availableHeight: number;
  count: number;
  chromeHeight?: number;
}): QuickRipLayout {
  const safeCount = Math.max(1, count);
  const singleRowWidth = solveQuickRipCardWidth({
    availableWidth,
    availableHeight,
    columns: safeCount,
    rows: 1,
    chromeHeight,
  });
  if (singleRowWidth >= SINGLE_ROW_MIN_W) {
    return { columns: safeCount, cardWidth: singleRowWidth };
  }

  const portraitRows = Math.ceil(safeCount / 2);
  const portraitHeightBound =
    (availableHeight - portraitRows * chromeHeight) /
    (portraitRows * CARD_ASPECT +
      ROW_FRAME_BLEED * Math.max(0, portraitRows - 1));
  if (safeCount > 2 && portraitHeightBound >= MIN_CARD_W) {
    return {
      columns: 2,
      cardWidth: solveQuickRipCardWidth({
        availableWidth,
        availableHeight,
        columns: 2,
        rows: portraitRows,
        chromeHeight,
      }),
    };
  }

  let columns = 1;
  let cardWidth = 0;
  for (let candidate = 1; candidate <= safeCount; candidate += 1) {
    const width = solveQuickRipCardWidth({
      availableWidth,
      availableHeight,
      columns: candidate,
      rows: Math.ceil(safeCount / candidate),
      chromeHeight,
    });
    if (width > cardWidth) {
      columns = candidate;
      cardWidth = width;
    }
  }
  return { columns, cardWidth };
}

function visibleHeight(element: HTMLElement): number {
  const styles = getComputedStyle(element);
  if (styles.display === "none") return 0;
  return (
    element.offsetHeight +
    (Number.parseFloat(styles.marginTop) || 0) +
    (Number.parseFloat(styles.marginBottom) || 0)
  );
}

function availableFanHeight(region: HTMLElement, wrap: HTMLElement): number {
  const regionStyles = getComputedStyle(region);
  const rowGap = Number.parseFloat(regionStyles.rowGap) || 0;
  const contentHeight =
    region.clientHeight -
    (Number.parseFloat(regionStyles.paddingTop) || 0) -
    (Number.parseFloat(regionStyles.paddingBottom) || 0);
  const visibleChildren = [...region.children].filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement &&
      getComputedStyle(child).display !== "none",
  );
  const used = visibleChildren.reduce(
    (total, child) => total + (child === wrap ? 0 : visibleHeight(child)),
    0,
  );
  return (
    contentHeight - used - Math.max(0, visibleChildren.length - 1) * rowGap
  );
}

function perCardChromeHeight(row: HTMLElement): number {
  const slot = row.querySelector<HTMLElement>(".rip-card-slot");
  const card = slot?.querySelector<HTMLElement>(".rip-card");
  if (!slot || !card) return 0;
  return Math.max(0, slot.offsetHeight - card.offsetHeight);
}

function outerPadding({
  availableHeight,
  cardWidth,
  columns,
  count,
  chromeHeight,
}: {
  availableHeight: number;
  cardWidth: number;
  columns: number;
  count: number;
  chromeHeight: number;
}): number {
  const rows = Math.ceil(count / Math.max(1, columns));
  const rowGap = ROW_FRAME_BLEED * cardWidth;
  const fanHeight =
    rows * (cardWidth * CARD_ASPECT + chromeHeight) +
    Math.max(0, rows - 1) * rowGap;
  return Math.min(
    Math.max(0, availableHeight - fanHeight) / 2,
    OUTER_FRAME_PAD * cardWidth,
  );
}

export type QuickRipFitter = {
  fit: () => void;
  destroy: () => void;
};

export function createQuickRipFitter({
  stage,
  region,
  wrap,
  row,
  cardCount,
}: {
  stage: () => HTMLElement | null;
  region: () => HTMLElement | null;
  wrap: () => HTMLElement | null;
  row: () => HTMLElement | null;
  cardCount: () => number;
}): QuickRipFitter {
  let frame = 0;
  let observer: ResizeObserver | null = null;
  let destroyed = false;

  const fitNow = () => {
    const stageElement = stage();
    const regionElement = region();
    const wrapElement = wrap();
    const rowElement = row();
    if (!stageElement || !regionElement || !wrapElement || !rowElement) return;

    const stageWidth = stageElement.clientWidth;
    const stageHeight = stageElement.clientHeight;
    const wrapWidth = wrapElement.clientWidth;
    const fanHeight = availableFanHeight(regionElement, wrapElement);
    if (stageWidth <= 0 || stageHeight <= 0 || wrapWidth <= 0 || fanHeight <= 0)
      return;

    const count = Math.max(1, cardCount());
    const chromeHeight = perCardChromeHeight(rowElement);
    const availableWidth = wrapWidth * FIT_SAFE;
    const availableHeight = fanHeight * FIT_SAFE;
    const layout = chooseQuickRipLayout({
      availableWidth,
      availableHeight,
      count,
      chromeHeight,
    });
    const scale = quickRipViewportScale(stageWidth, stageHeight);
    const cardWidth = Math.min(layout.cardWidth, MAX_CARD_W * scale);
    const columnGap = GAP_FRAME_BLEED * cardWidth;
    const rowGap = ROW_FRAME_BLEED * cardWidth;

    stageElement.style.setProperty("--quick-scale", scale.toFixed(3));
    wrapElement.style.setProperty("--quick-card-w", `${cardWidth}px`);
    wrapElement.dataset.columns = String(layout.columns);
    wrapElement.style.paddingBlock = `${outerPadding({
      availableHeight,
      cardWidth,
      columns: layout.columns,
      count,
      chromeHeight,
    })}px`;
    rowElement.style.columnGap = `${columnGap}px`;
    rowElement.style.rowGap = `${rowGap}px`;
    rowElement.style.maxWidth = `${
      layout.columns * cardWidth + Math.max(0, layout.columns - 1) * columnGap
    }px`;
  };

  const schedule = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      if (!destroyed) fitNow();
    });
  };

  const regionElement = region();
  if (regionElement && typeof ResizeObserver !== "undefined") {
    observer = new ResizeObserver(schedule);
    observer.observe(regionElement);
  } else {
    window.addEventListener("resize", schedule);
  }
  window.visualViewport?.addEventListener("resize", schedule);
  fitNow();

  return {
    fit: schedule,
    destroy: () => {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      if (!observer) window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    },
  };
}
